import {
  AlertCircle,
  ArrowUpRight,
  BarChart3,
  Check,
  Heart,
  Instagram,
  LoaderCircle,
  MessageCircle,
  Play,
  RefreshCw,
  Share2,
  Sparkles,
  ThumbsDown,
  ThumbsUp,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import {
  generateReelAnalysis,
  getResearchReels,
  ResearchApiError,
  saveReelFeedback,
  type ResearchDetailedAnalysis,
  type ResearchFeedback,
  type ResearchReel,
  type ResearchReelListResponse,
} from "./research-api";
import "./research-lab.css";

const demoAnalysis: ResearchDetailedAnalysis = {
  method: "deterministic_v1",
  generatedAt: "2026-07-29T12:00:00.000Z",
  summary:
    "A fast product reveal earns attention before the creator explains the payoff. The edit keeps every beat focused on one clear transformation.",
  hook: {
    type: "proof",
    evidence: "The visible result appears before the explanation.",
    whyItWorks:
      "Showing the payoff immediately creates curiosity about how the product produced it.",
  },
  format: {
    type: "product_demo",
    structure: [
      "Lead with the customer result",
      "Reveal the product in use",
      "Close on one proof point",
    ],
    pacing: "A new visual beat lands every two to three seconds.",
  },
  momentum: {
    score: 88,
    label: "breakout",
    rationale:
      "Views and shares are accelerating well above the creator's recent baseline.",
    signals: [
      "Strong early view velocity",
      "High save-to-like ratio",
      "Performance is an account-level outlier",
    ],
  },
  audience: {
    primaryAudience: "Product-aware shoppers looking for visible proof",
    intent: "high",
    commentThemes: ["Results", "Product use", "Where to buy"],
    questions: ["How quickly does it work?", "Where can I get this?"],
  },
  remixBrief: {
    angle: "Make your strongest customer result the story's opening frame.",
    originalHook: "This is what changed after one week with our product.",
    beats: [
      "Show the finished result",
      "Rewind to the original problem",
      "Demonstrate the product",
      "Return to the proof and call to action",
    ],
    captionDirection:
      "Write a short customer-first caption that adds context without repeating the video.",
    callToAction: "Save this for the next time you face the same problem.",
  },
};

const demoReels: ResearchReel[] = [
  {
    id: "demo-product-proof",
    provider: "demo",
    sourceMediaId: "demo-product-proof",
    mediaUrl: "/assets/reels/product-proof.mp4",
    thumbnailUrl: null,
    permalink: "https://www.instagram.com/reels/",
    username: "proof.driven.brand",
    caption: "The product result leads. Everything else earns the next second.",
    publishedAt: "2026-07-28T09:30:00.000Z",
    durationSeconds: 19,
    metrics: {
      views: 842000,
      plays: 842000,
      likes: 68400,
      comments: 1240,
      shares: 9800,
      saves: 17500,
      reach: 791000,
      accountFollowers: 124000,
    },
    momentum: {
      score: 94,
      label: "breakout",
      viewVelocityPerHour: 35600,
      engagementRate: 0.094,
      outlierMultiplier: 8.4,
    },
    feedback: null,
    analysisStatus: "ready",
    detailedAnalysis: demoAnalysis,
  },
  {
    id: "demo-customer-story",
    provider: "demo",
    sourceMediaId: "demo-customer-story",
    mediaUrl: "/assets/reels/customer-story.mp4",
    thumbnailUrl: null,
    permalink: "https://www.instagram.com/reels/",
    username: "everyday.customer",
    caption: "A familiar problem becomes a customer-led story in 18 seconds.",
    publishedAt: "2026-07-27T16:10:00.000Z",
    durationSeconds: 18,
    metrics: {
      views: 416000,
      plays: 416000,
      likes: 32100,
      comments: 860,
      shares: 5200,
      saves: 7900,
      reach: 382000,
      accountFollowers: 91000,
    },
    momentum: {
      score: 87,
      label: "breakout",
      viewVelocityPerHour: 17400,
      engagementRate: 0.092,
      outlierMultiplier: 5.1,
    },
    feedback: null,
    analysisStatus: "ready",
    detailedAnalysis: {
      ...demoAnalysis,
      hook: {
        type: "problem",
        evidence: "The first line names a familiar customer frustration.",
        whyItWorks:
          "Plain-language recognition makes the right viewer feel immediately understood.",
      },
    },
  },
  {
    id: "demo-product-animation",
    provider: "demo",
    sourceMediaId: "demo-product-animation",
    mediaUrl: "/assets/reels/product-animation.mp4",
    thumbnailUrl: null,
    permalink: "https://www.instagram.com/reels/",
    username: "motion.sells",
    caption: "Motion makes one product benefit instantly understandable.",
    publishedAt: "2026-07-26T12:45:00.000Z",
    durationSeconds: 16,
    metrics: {
      views: 296000,
      plays: 296000,
      likes: 24400,
      comments: 430,
      shares: 4100,
      saves: 6800,
      reach: 274000,
      accountFollowers: 68000,
    },
    momentum: {
      score: 81,
      label: "rising",
      viewVelocityPerHour: 10900,
      engagementRate: 0.101,
      outlierMultiplier: 4.2,
    },
    feedback: null,
    analysisStatus: "ready",
    detailedAnalysis: {
      ...demoAnalysis,
      hook: {
        type: "proof",
        evidence: "The animation makes the core product benefit visible.",
        whyItWorks:
          "The viewer understands the change without needing sound or a long setup.",
      },
    },
  },
  {
    id: "demo-founder-explainer",
    provider: "demo",
    sourceMediaId: "demo-founder-explainer",
    mediaUrl: "/assets/reels/founder-explainer.mp4",
    thumbnailUrl: null,
    permalink: "https://www.instagram.com/reels/",
    username: "founder.notes",
    caption: "One contrarian product belief, explained without a long setup.",
    publishedAt: "2026-07-25T08:20:00.000Z",
    durationSeconds: 24,
    metrics: {
      views: 191000,
      plays: 191000,
      likes: 14800,
      comments: 610,
      shares: 2300,
      saves: 4100,
      reach: 176000,
      accountFollowers: 52000,
    },
    momentum: {
      score: 76,
      label: "rising",
      viewVelocityPerHour: 7100,
      engagementRate: 0.084,
      outlierMultiplier: 3.7,
    },
    feedback: null,
    analysisStatus: "ready",
    detailedAnalysis: demoAnalysis,
  },
  {
    id: "demo-founder-story",
    provider: "demo",
    sourceMediaId: "demo-founder-story",
    mediaUrl: "/assets/reels/founder-story.mp4",
    thumbnailUrl: null,
    permalink: "https://www.instagram.com/reels/",
    username: "building.in.public",
    caption: "The brand origin is told through one decision, not a full timeline.",
    publishedAt: "2026-07-24T18:00:00.000Z",
    durationSeconds: 26,
    metrics: {
      views: 128000,
      plays: 128000,
      likes: 9600,
      comments: 380,
      shares: 1700,
      saves: 2900,
      reach: 114000,
      accountFollowers: 44000,
    },
    momentum: {
      score: 69,
      label: "established",
      viewVelocityPerHour: 4200,
      engagementRate: 0.078,
      outlierMultiplier: 2.9,
    },
    feedback: null,
    analysisStatus: "ready",
    detailedAnalysis: demoAnalysis,
  },
];

const compactNumber = new Intl.NumberFormat("en", {
  notation: "compact",
  maximumFractionDigits: 1,
});

const formatMetric = (value: number | null): string =>
  value === null ? "—" : compactNumber.format(value);

type ResearchLabProps = {
  onConnectInstagram?: () => void;
};

type AnalysisPanelState = {
  analysis: ResearchDetailedAnalysis;
  caption: string;
  username: string;
};

const LoadingState = () => (
  <div className="research-state" role="status" aria-live="polite">
    <span className="research-state__icon">
      <LoaderCircle className="is-spinning" size={21} aria-hidden="true" />
    </span>
    <h2>Finding breakout Reels</h2>
    <p>Reading your niche signals and ranking the strongest opportunities.</p>
  </div>
);

const SetupState = ({
  message,
  onConnect,
}: {
  message?: string;
  onConnect: () => void;
}) => (
  <div className="research-state">
    <span className="research-state__icon is-instagram">
      <Instagram size={21} aria-hidden="true" />
    </span>
    <span className="research-state__eyebrow">One connection needed</span>
    <h2>Connect Instagram to start your live research feed.</h2>
    <p>
      {message ??
        "Reelio will monitor the accounts and signals relevant to your business. You stay in control of every idea it saves."}
    </p>
    <button type="button" onClick={onConnect}>
      <Instagram size={16} aria-hidden="true" />
      Connect Instagram
    </button>
  </div>
);

const EmptyState = ({ onRetry }: { onRetry: () => void }) => (
  <div className="research-state">
    <span className="research-state__icon">
      <BarChart3 size={21} aria-hidden="true" />
    </span>
    <h2>No trend matches yet.</h2>
    <p>
      Reelio is watching your niche. Add competitor accounts or check again
      after the next scan.
    </p>
    <button type="button" onClick={onRetry}>
      <RefreshCw size={16} aria-hidden="true" />
      Scan again
    </button>
  </div>
);

const AnalysisPanel = ({
  panel,
  onClose,
}: {
  panel: AnalysisPanelState;
  onClose: () => void;
}) => {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  return (
    <div className="research-analysis" role="presentation" onMouseDown={onClose}>
      <section
        className="research-analysis__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="research-analysis-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header>
          <div>
            <span>
              <Sparkles size={13} aria-hidden="true" />
              Detailed analysis
            </span>
            <h2 id="research-analysis-title">Why this Reel is working</h2>
            <p>@{panel.username}</p>
          </div>
          <button type="button" aria-label="Close analysis" onClick={onClose}>
            <X size={18} aria-hidden="true" />
          </button>
        </header>

        <div className="research-analysis__body">
          <section>
            <h3>Agent read</h3>
            <p>{panel.analysis.summary}</p>
          </section>
          <section className="research-analysis__hook">
            <h3>Opening hook</h3>
            <p>“{panel.analysis.hook.evidence}”</p>
            <small>{panel.analysis.hook.whyItWorks}</small>
          </section>
          <section>
            <h3>Why it works</h3>
            <ul>
              {panel.analysis.momentum.signals.map((reason) => (
                <li key={reason}>
                  <Check size={14} aria-hidden="true" />
                  <span>{reason}</span>
                </li>
              ))}
            </ul>
          </section>
          <section>
            <h3>Audience signals</h3>
            <ul>
              {panel.analysis.audience.commentThemes.map((signal) => (
                <li key={signal}>
                  <MessageCircle size={14} aria-hidden="true" />
                  <span>{signal}</span>
                </li>
              ))}
            </ul>
          </section>
          <section>
            <h3>Ways your brand can remix it</h3>
            <ol>
              {panel.analysis.remixBrief.beats.map((idea) => (
                <li key={idea}>{idea}</li>
              ))}
            </ol>
          </section>
          <section className="research-analysis__source">
            <h3>Source caption</h3>
            <p>{panel.caption}</p>
          </section>
        </div>
      </section>
    </div>
  );
};

export const ResearchLab = ({ onConnectInstagram }: ResearchLabProps) => {
  const [feed, setFeed] = useState<ResearchReelListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadNotice, setLoadNotice] = useState<string | null>(null);
  const [instagramSetupMessage, setInstagramSetupMessage] = useState<
    string | null
  >(null);
  const [analyzingId, setAnalyzingId] = useState<string | null>(null);
  const [savingFeedbackId, setSavingFeedbackId] = useState<string | null>(null);
  const [cardErrors, setCardErrors] = useState<Record<string, string>>({});
  const [panel, setPanel] = useState<AnalysisPanelState | null>(null);

  const loadFeed = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setLoadNotice(null);

    try {
      const nextFeed = await getResearchReels(signal);
      if (
        nextFeed.providerStatus.state === "setup_required" &&
        nextFeed.items.length === 0
      ) {
        setInstagramSetupMessage(nextFeed.providerStatus.message);
        setFeed({
          items: demoReels,
          nextCursor: null,
          providerStatus: {
            provider: "demo",
            state: "degraded",
            message: nextFeed.providerStatus.message,
            lastSyncedAt: null,
          },
        });
      } else {
        setInstagramSetupMessage(null);
        setFeed(nextFeed);
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      const message =
        error instanceof ResearchApiError
          ? error.message
          : "The live Instagram feed could not be reached.";
      setFeed({
        items: demoReels,
        nextCursor: null,
        providerStatus: {
          provider: "demo",
          state: "degraded",
          message,
          lastSyncedAt: null,
        },
      });
      setInstagramSetupMessage(null);
      setLoadNotice(`${message} Showing a safe demo feed for now.`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void loadFeed(controller.signal);
    return () => controller.abort();
  }, [loadFeed]);

  const reelLookup = useMemo(
    () => new Map(feed?.items.map((reel) => [reel.id, reel])),
    [feed],
  );

  const connectInstagram = () => {
    if (onConnectInstagram) {
      onConnectInstagram();
      return;
    }
    window.location.assign("/api/instagram/connect");
  };

  const setFeedback = async (
    reelId: string,
    current: ResearchFeedback | null,
    sentiment: ResearchFeedback,
  ) => {
    if (!feed || savingFeedbackId) return;
    const next = sentiment;
    if (current === next) return;
    const previousReels = feed.items;
    setSavingFeedbackId(reelId);
    setCardErrors((errors) => ({ ...errors, [reelId]: "" }));
    setFeed({
      ...feed,
      items: feed.items.map((reel) =>
        reel.id === reelId ? { ...reel, feedback: next } : reel,
      ),
    });

    if (feed.providerStatus.provider === "demo") {
      setSavingFeedbackId(null);
      return;
    }

    try {
      const saved = await saveReelFeedback(reelId, next);
      setFeed((currentFeed) =>
        currentFeed
          ? {
              ...currentFeed,
              items: currentFeed.items.map((reel) =>
                reel.id === reelId ? { ...reel, feedback: saved } : reel,
              ),
            }
          : currentFeed,
      );
    } catch (error) {
      setFeed((currentFeed) =>
        currentFeed ? { ...currentFeed, items: previousReels } : currentFeed,
      );
      setCardErrors((errors) => ({
        ...errors,
        [reelId]:
          error instanceof Error
            ? error.message
            : "Your feedback could not be saved.",
      }));
    } finally {
      setSavingFeedbackId(null);
    }
  };

  const analyze = async (reel: ResearchReel) => {
    if (analyzingId) return;
    setAnalyzingId(reel.id);
    setCardErrors((errors) => ({ ...errors, [reel.id]: "" }));

    if (feed?.providerStatus.provider === "demo" && reel.detailedAnalysis) {
      window.setTimeout(() => {
        setPanel({
          analysis: reel.detailedAnalysis ?? demoAnalysis,
          caption: reel.caption,
          username: reel.username,
        });
        setAnalyzingId(null);
      }, 700);
      return;
    }

    try {
      const analysis = await generateReelAnalysis(reel.id);
      setFeed((currentFeed) =>
        currentFeed
          ? {
              ...currentFeed,
              items: currentFeed.items.map((item) =>
                item.id === reel.id
                  ? {
                      ...item,
                      analysisStatus: "ready" as const,
                      detailedAnalysis: analysis,
                    }
                  : item,
              ),
            }
          : currentFeed,
      );
      setPanel({
        analysis,
        caption: reel.caption,
        username: reel.username,
      });
    } catch (error) {
      setCardErrors((errors) => ({
        ...errors,
        [reel.id]:
          error instanceof Error
            ? error.message
            : "Detailed analysis could not be generated.",
      }));
    } finally {
      setAnalyzingId(null);
    }
  };

  if (loading) {
    return (
      <section className="research-lab" aria-label="Research lab">
        <LoadingState />
      </section>
    );
  }

  if (feed?.providerStatus.state === "setup_required") {
    return (
      <section className="research-lab" aria-label="Research lab">
        <SetupState
          message={feed.providerStatus.message}
          onConnect={connectInstagram}
        />
      </section>
    );
  }

  if (!feed?.items.length) {
    return (
      <section className="research-lab" aria-label="Research lab">
        <EmptyState onRetry={() => void loadFeed()} />
      </section>
    );
  }

  const providerNotice =
    (instagramSetupMessage
      ? `${instagramSetupMessage} Showing an interactive sample feed until it is connected.`
      : loadNotice) ??
    (feed.providerStatus.state === "degraded" ||
    feed.providerStatus.state === "error"
      ? feed.providerStatus.message
      : null);

  return (
    <section className="research-lab" aria-label="Research lab">
      <div className="research-lab__intro">
        <div>
          <span className="research-lab__eyebrow">
            <Sparkles size={13} aria-hidden="true" />
            Ranked for your brand
          </span>
          <h2>Reels worth studying now</h2>
          <p>
            Like useful signals to shape the next scan. Open an analysis to
            understand the hook, audience response, and repeatable pattern.
          </p>
        </div>
        <span className="research-lab__count">
          {feed.items.length} opportunities
        </span>
      </div>

      {providerNotice ? (
        <div className="research-lab__notice" role="alert">
          <AlertCircle size={16} aria-hidden="true" />
          <span>{providerNotice}</span>
          <button
            type="button"
            onClick={
              instagramSetupMessage
                ? connectInstagram
                : () => void loadFeed()
            }
          >
            {instagramSetupMessage ? "Connect Instagram" : "Retry live feed"}
          </button>
        </div>
      ) : null}

      <div className="research-grid">
        {feed.items.map((reel) => {
          const activeFeedback = reel.feedback;
          const analyzing = analyzingId === reel.id;
          const saving = savingFeedbackId === reel.id;
          const sourceReel = reelLookup.get(reel.id);

          return (
            <article className="research-card" key={reel.id}>
              <div className="research-card__media">
                {reel.mediaUrl ? (
                  <video
                    aria-label={`Reel by ${reel.username}`}
                    controls
                    loop
                    muted
                    playsInline
                    preload="metadata"
                    poster={reel.thumbnailUrl ?? undefined}
                    src={reel.mediaUrl}
                  />
                ) : (
                  <div className="research-card__media-unavailable">
                    <Instagram size={24} aria-hidden="true" />
                    <span>Preview unavailable</span>
                    <small>Open the source Reel to watch</small>
                  </div>
                )}
                <span className="research-card__plays">
                  <Play size={12} fill="currentColor" aria-hidden="true" />
                  {formatMetric(reel.metrics.plays ?? reel.metrics.views)}
                </span>
                <a
                  className="research-card__source"
                  href={reel.permalink}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`Open source Reel by ${reel.username}`}
                  title="Open source Reel"
                >
                  <ArrowUpRight size={15} aria-hidden="true" />
                </a>
              </div>

              <div className="research-card__content">
                <div className="research-card__creator">
                  <span>@{reel.username}</span>
                  <time dateTime={reel.publishedAt}>
                    {new Date(reel.publishedAt).toLocaleDateString("en", {
                      day: "numeric",
                      month: "short",
                    })}
                  </time>
                </div>
                <p>{reel.caption}</p>

                <div className="research-card__metrics" aria-label="Reel metrics">
                  <span title="Likes">
                    <Heart size={14} aria-hidden="true" />
                    {formatMetric(reel.metrics.likes)}
                  </span>
                  <span title="Comments">
                    <MessageCircle size={14} aria-hidden="true" />
                    {formatMetric(reel.metrics.comments)}
                  </span>
                  <span title="Shares">
                    <Share2 size={14} aria-hidden="true" />
                    {formatMetric(reel.metrics.shares)}
                  </span>
                </div>

                <div className="research-card__actions">
                  <div aria-label="Rate this research signal">
                    <button
                      type="button"
                      className={activeFeedback === "like" ? "is-active" : ""}
                      aria-label="Like this Reel"
                      aria-pressed={activeFeedback === "like"}
                      disabled={saving}
                      onClick={() =>
                        void setFeedback(reel.id, activeFeedback, "like")
                      }
                    >
                      <ThumbsUp size={15} aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      className={
                        activeFeedback === "dislike"
                          ? "is-active is-dislike"
                          : ""
                      }
                      aria-label="Dislike this Reel"
                      aria-pressed={activeFeedback === "dislike"}
                      disabled={saving}
                      onClick={() =>
                        void setFeedback(reel.id, activeFeedback, "dislike")
                      }
                    >
                      <ThumbsDown size={15} aria-hidden="true" />
                    </button>
                  </div>
                  <button
                    type="button"
                    className="research-card__analyze"
                    disabled={Boolean(analyzingId)}
                    onClick={() => void analyze(sourceReel ?? reel)}
                  >
                    {analyzing ? (
                      <LoaderCircle
                        className="is-spinning"
                        size={15}
                        aria-hidden="true"
                      />
                    ) : (
                      <Sparkles size={15} aria-hidden="true" />
                    )}
                    {analyzing ? "Analyzing…" : "Generate detailed analysis"}
                  </button>
                </div>

                {cardErrors[reel.id] ? (
                  <p className="research-card__error" role="alert">
                    <AlertCircle size={13} aria-hidden="true" />
                    {cardErrors[reel.id]}
                  </p>
                ) : null}
              </div>
            </article>
          );
        })}
      </div>

      {panel ? (
        <AnalysisPanel panel={panel} onClose={() => setPanel(null)} />
      ) : null}
    </section>
  );
};
